import { ApiError, useApiMutation, useApiQuery } from '../../../api/useApi';
import { MutationOptions } from 'react-query';

// #################################################
//                      TYPES
// #################################################

export type ChangelogMediaItem = {
    type: 'image' | 'video';
    url: string;
    caption?: string;
};

export type ChangelogEntry = {
    id: number;
    title: string;
    slug: string;
    description: string | null;
    type: 'feature' | 'improvement' | 'fix' | 'change';
    category: string;
    min_subscription_id: number | null;
    subscription_name: string | null;
    published_at: string | null;
    is_pinned: boolean;
    internal_link: string | null;
    media: ChangelogMediaItem[] | null;
    locale: string;
    is_new: boolean;
};

export type PaginatedChangelogs = {
    data: ChangelogEntry[];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number | null;
        to: number | null;
    };
};

export type ChangelogFilters = {
    page?: number;
    per_page?: number;
    search?: string;
    category?: string;
    type?: string;
    min_subscription_id?: string;
    date_from?: string;
    date_to?: string;
    for_my_plan?: boolean;
};

// #################################################
//                      QUERIES
// #################################################

export function useChangelogs(filters: ChangelogFilters = {}) {
    const params = new URLSearchParams();

    if (filters.page) params.set('page', String(filters.page));
    if (filters.per_page) params.set('per_page', String(filters.per_page));
    if (filters.search) params.set('search', filters.search);
    if (filters.category) params.set('category', filters.category);
    if (filters.type) params.set('type', filters.type);
    if (filters.min_subscription_id) params.set('min_subscription_id', filters.min_subscription_id);
    if (filters.date_from) params.set('date_from', filters.date_from);
    if (filters.date_to) params.set('date_to', filters.date_to);
    if (filters.for_my_plan) params.set('for_my_plan', '1');

    const queryString = params.toString();
    const url = `/changelogs${queryString ? `?${queryString}` : ''}`;

    return useApiQuery<PaginatedChangelogs>(url);
}

export function useChangelogUnreadCount(enabled: boolean = true) {
    return useApiQuery<{ unread_count: number }>('/changelogs/unread-count', {}, {
        staleTime: 0,
        refetchOnWindowFocus: false,
        retry: false,
        enabled,
    });
}

export function useChangelogCategories() {
    return useApiQuery<string[]>('/changelogs/categories');
}

// #################################################
//                     MUTATIONS
// #################################################

export function useMarkChangelogRead(options: MutationOptions<{ message: string }, ApiError, void> = {}) {
    return useApiMutation<{ message: string }, ApiError, void>(
        'post',
        () => '/changelogs/mark-read',
        {
            ...options,
        }
    );
}

export function useMarkSingleChangelogRead(options: MutationOptions<{ message: string }, ApiError, number> = {}) {
    return useApiMutation<{ message: string }, ApiError, number>(
        'post',
        (id) => `/changelogs/${id}/mark-read`,
        {
            ...options,
        }
    );
}
